import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { BdcDispatchDay, BdcDispatchDc } from "../../../types/bdc.type";
import { pcs, thaiDateTime } from "../format";
import { CARD_SX, TagTone } from "../styles";
import Tag from "../Tag";
import { STATUS_LABEL, traysText } from "./record";

const STATUS_TONE: Record<BdcDispatchDc["status"], TagTone> = {
  not_printed: "mut",
  printed: "info",
  recorded: "ok",
};

/** สถานะ + จำนวนครั้งที่พิมพ์ + เตือนยอดเปลี่ยนหลังพิมพ์ */
function StatusCell({ dc }: Readonly<{ dc: BdcDispatchDc }>) {
  const when = dc.status === "recorded" ? dc.recorded_at : dc.printed_at;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, alignItems: "flex-start" }}>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
        <Tag tone={STATUS_TONE[dc.status]}>{STATUS_LABEL[dc.status]}</Tag>
        {dc.is_changed && <Tag tone="warn">ยอดเปลี่ยนหลังพิมพ์</Tag>}
        {dc.has_short && <Tag tone="crit">มีขาด</Tag>}
      </Box>
      {dc.print_count > 0 && (
        <span className="sub">
          พิมพ์ {dc.print_count} ครั้ง{when ? ` · ${thaiDateTime(when)}` : ""}
        </span>
      )}
    </Box>
  );
}

/**
 * DC ของวันนี้ — one row per DC with เบิก, ถาด and the note's status. The desk ticks DCs to
 * print; a click on the row opens that DC's note below (a DC not printed yet has none).
 */
export default function DcTable({
  day,
  selected,
  selectable,
  activeDc,
  onToggle,
  onToggleAll,
  onOpen,
}: Readonly<{
  day: BdcDispatchDay;
  selected: ReadonlySet<string>;
  selectable: boolean;
  activeDc: string | null;
  onToggle: (dcCode: string) => void;
  onToggleAll: (checked: boolean) => void;
  onOpen: (dc: BdcDispatchDc) => void;
}>) {
  const all = day.dcs.length > 0 && day.dcs.every((dc) => selected.has(dc.dc_code));
  const some = !all && day.dcs.some((dc) => selected.has(dc.dc_code));
  return (
    <Card sx={{ ...CARD_SX, p: 1.75, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
      <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>DC ของวันนี้</Typography>
      {day.dcs.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          ไม่มี DC ที่มียอดเบิกในวันสั่งนี้ — นำเข้าไฟล์ ORDS ของวันนี้ก่อน
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <table className="bdc-t" data-testid="bdc-dispatch-dcs">
            <thead>
              <tr>
                {selectable && (
                  <th style={{ width: 36 }}>
                    <Checkbox
                      size="small"
                      checked={all}
                      indeterminate={some}
                      onChange={(e) => onToggleAll(e.target.checked)}
                      inputProps={{ "aria-label": "เลือกทั้งหมด" }}
                      sx={{ p: 0 }}
                    />
                  </th>
                )}
                <th>DC</th>
                <th className="n">เบิก (ชิ้น)</th>
                <th className="n">ถาด</th>
                <th className="n">ขาด</th>
                <th>สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {day.dcs.map((dc) => (
                <tr
                  key={dc.dc_code}
                  className="click"
                  onClick={() => onOpen(dc)}
                  style={activeDc === dc.dc_code ? { background: "#FFF8EA" } : undefined}
                >
                  {selectable && (
                    <td onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        size="small"
                        checked={selected.has(dc.dc_code)}
                        onChange={() => onToggle(dc.dc_code)}
                        inputProps={{ "aria-label": `เลือก ${dc.dc_code}` }}
                        sx={{ p: 0 }}
                      />
                    </td>
                  )}
                  <td>
                    <span className="dc">{dc.dc_code}</span> {dc.dc_name}
                    {dc.driver_name && <div className="sub">คนขับ {dc.driver_name}</div>}
                  </td>
                  <td className="n">{pcs(dc.issued_qty)}</td>
                  <td className="n" title="ถาดเต็ม + เศษชิ้น">
                    {traysText(dc.trays, dc.pieces)}
                  </td>
                  <td className="n">{dc.short_qty === null ? "-" : pcs(dc.short_qty)}</td>
                  <td>
                    <StatusCell dc={dc} />
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                {selectable && <td />}
                <td>รวม {day.dcs.length} DC</td>
                <td className="n">{pcs(day.totals.issued_qty)}</td>
                <td className="n">{traysText(day.totals.trays, day.totals.pieces)}</td>
                <td />
                <td />
              </tr>
            </tfoot>
          </table>
        </Box>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
        เบิก = ยอดส่งของ DC รวมทุกรอบของวันสั่งนี้ · ถาด = ถาดเต็มตามชิ้นต่อถาดของสินค้า + เศษชิ้น · ใบที่พิมพ์แล้ว
        แสดงตัวเลขตามที่พิมพ์ — "ยอดเปลี่ยนหลังพิมพ์" = นำเข้าไฟล์ใหม่หลังพิมพ์ ใบที่ยังไม่บันทึกพิมพ์ซ้ำได้เพื่อให้ตรงกับยอดล่าสุด
        (ใบที่บันทึกแล้วพิมพ์ตามที่บันทึก)
      </Typography>
    </Card>
  );
}
